import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { QueryRubricButton } from './QueryRubricDialog';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import { getQuizConfig } from '../../config/quizTypes';
import AttachmentViewer from '../AttachmentViewer';
import { FiCheck, FiAlertTriangle, FiFileText } from 'react-icons/fi';

const SingleChoiceQuiz = ({
    quiz,
    onSubmit,
    participation,
    isSubmitting,
    initialSelection = [],
    onSelectionChange,
    challengeId,  // 챌린지 ID 추가
    submitMessage,  // 부모로부터 제출 결과 메시지 받기
    initialTimeLeft,  // 부모로부터 받은 저장된 타이머 시간
    onTimeUpdate  // 타이머 업데이트 콜백
}) => {
    const { t } = useTranslation();
    const [selectedChoices, setSelectedChoices] = useState([]); // 단일 → 다중 선택
    const [quizData, setQuizData] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const prevQuizKeyRef = useRef(null); // 이전 퀴즈 키(챌린지ID+퀴즈번호) 추적
    const hasRestoredRef = useRef(false); // 이미 복원했는지 추적
    const selectedChoicesRef = useRef(selectedChoices); // 타이머에서 최신 선택 참조용
    const onSubmitRef = useRef(onSubmit); // 타이머에서 최신 onSubmit 참조용

    const config = getQuizConfig('002');
    const hasParticipated = participation && participation.SUCCSS_YN !== undefined;
    const isCorrect = hasParticipated && participation.SUCCSS_YN === 'Y';

    // 제출 횟수 관련
    // SBMSN_NOT 필드가 없으면 hasParticipated로 판단 (1회 제출로 간주)
    const submissionCount = participation?.SBMSN_NOT ?? (hasParticipated ? 1 : 0);
    const maxSubmissions = config.maxSubmissions || 1; // 최대 제출 가능 횟수
    const remainingSubmissions = Math.max(0, maxSubmissions - submissionCount); // 잔여 제출 횟수
    const canSubmit = remainingSubmissions > 0 && !isCorrect; // 제출 가능 여부

    // ref 업데이트 (타이머에서 최신 값 참조용)
    useEffect(() => {
        selectedChoicesRef.current = selectedChoices;
    }, [selectedChoices]);

    useEffect(() => {
        onSubmitRef.current = onSubmit;
    }, [onSubmit]);

    // 퀴즈 데이터 파싱 - QUIZ_DC는 마크다운 텍스트로 문제와 선택항목 모두 포함
    useEffect(() => {
        if (quiz) {
            try {
                // QUIZ_DC는 마크다운 텍스트 (문제 내용과 선택항목 포함)
                let questionMarkdown = quiz.QUIZ_DC || '';

                // 줄바꿈이 없는 경우에만 복원 처리
                if (!questionMarkdown.includes('\n')) {
                    // 1. 첫 번째 헤딩은 그대로, 두 번째 헤딩부터 앞에 줄바꿈 추가
                    let firstHeading = true;
                    questionMarkdown = questionMarkdown.replace(/(##\s+)/g, (match) => {
                        if (firstHeading) {
                            firstHeading = false;
                            return match;
                        }
                        return '\n\n' + match;
                    });

                    // 2. "- **T" 또는 "- **[숫자]" 패턴 앞에 줄바꿈 추가 (단, balance - 같은 건 제외)
                    questionMarkdown = questionMarkdown.replace(/(\s)-\s+(\*\*T\d)/g, '\n- $2');

                    // 3. 앞뒤 공백 제거
                    questionMarkdown = questionMarkdown.trim();
                }

                // CRANS_RST_CN(정답결과내용)에서 선택항목 JSON 파싱
                let choices = [];
                let correctAnswers = [];

                if (quiz.CRANS_RST_CN) {
                    try {
                        // JSON 문자열 정제: 불필요한 공백 및 잘못된 형식 제거
                        let jsonString = quiz.CRANS_RST_CN.trim();

                        // 후행 쉼표 제거 (예: }, ] 사이의 공백 제거)
                        jsonString = jsonString.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');

                        const resultData = JSON.parse(jsonString);
                        const rawChoices = resultData.choices || [];

                        // isAnswer 필드 포함하여 파싱
                        choices = rawChoices.map(choice => ({
                            id: choice.id,
                            text: choice.text,
                            isAnswer: choice.isAnswer
                        }));

                        // 정답 ID 목록 추출
                        correctAnswers = rawChoices
                            .filter(choice => choice.isAnswer === 'Y')
                            .map(choice => choice.id);
                    } catch (parseError) {
                        console.error('❌ CRANS_RST_CN 파싱 오류:', parseError);
                        // 기본 선택지로 폴백
                        choices = [
                            { id: '1', text: t('challenge:quiz.opt1'), isAnswer: 'N' },
                            { id: '2', text: t('challenge:quiz.opt2'), isAnswer: 'N' },
                            { id: '3', text: t('challenge:quiz.opt3'), isAnswer: 'N' },
                            { id: '4', text: t('challenge:quiz.opt4'), isAnswer: 'N' }
                        ];
                        correctAnswers = [];
                    }
                } else {
                    // CRANS_RST_CN이 없는 경우 기본 선택지
                    choices = [
                        { id: '1', text: t('challenge:quiz.opt1'), isAnswer: 'N' },
                        { id: '2', text: t('challenge:quiz.opt2'), isAnswer: 'N' },
                        { id: '3', text: t('challenge:quiz.opt3'), isAnswer: 'N' },
                        { id: '4', text: t('challenge:quiz.opt4'), isAnswer: 'N' }
                    ];
                    correctAnswers = [];
                }

                setQuizData({
                    question: questionMarkdown,
                    choices: choices,
                    correctAnswers: correctAnswers  // 정답 목록 저장
                });
            } catch (error) {
                console.error('❌ 퀴즈 데이터 처리 오류:', error);
                // 에러 발생 시 기본값
                setQuizData({
                    question: quiz.QUIZ_DC || t('challenge:quiz.loadFailed'),
                    choices: [
                        { id: 'A', text: t('challenge:quiz.optA') },
                        { id: 'B', text: t('challenge:quiz.optB') },
                        { id: 'C', text: t('challenge:quiz.optC') },
                        { id: 'D', text: t('challenge:quiz.optD') }
                    ]
                });
            }
        }
    }, [quiz, hasParticipated]);

    // 타이머 초기화 (퀴즈 변경 시에만)
    useEffect(() => {
        if (!hasParticipated && config.timeLimit) {
            // initialTimeLeft가 있으면 그 값부터 시작, 없으면 config.timeLimit부터 시작
            const startTime = initialTimeLeft !== undefined ? initialTimeLeft : config.timeLimit;
            setTimeLeft(startTime);
        }
    }, [challengeId, quiz?.QUIZ_NO]);

    // 타이머 작동 (참여하지 않은 경우에만)
    useEffect(() => {
        if (!hasParticipated && config.timeLimit && timeLeft !== null && timeLeft > 0) {
            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        // 시간 초과 시 자동 제출 (실패로 처리)
                        const currentChoices = selectedChoicesRef.current;
                        const choicesString = currentChoices.join(',') || '';

                        console.log('⏰ [타임아웃] 시간 초과로 자동 제출:', {
                            hasParticipated,
                            currentChoices,
                            choicesString
                        });

                        if (!hasParticipated && onSubmitRef.current) {
                            console.log('⏰ [타임아웃] onSubmit 호출:', choicesString);
                            onSubmitRef.current(choicesString);
                        } else {
                            console.log('⏰ [타임아웃] 제출 건너뛰기:', { hasParticipated, hasOnSubmit: !!onSubmitRef.current });
                        }

                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [hasParticipated, timeLeft, config.timeLimit]);

    // 타이머 업데이트를 부모에게 알림 (별도 useEffect로 분리하여 렌더링 사이클 문제 해결)
    useEffect(() => {
        if (onTimeUpdate && timeLeft !== null) {
            onTimeUpdate(timeLeft);
        }
    }, [timeLeft, onTimeUpdate]);

    // 선택 상태 초기화 및 복원
    useEffect(() => {
        const currentQuizKey = challengeId && quiz?.QUIZ_NO ? `${challengeId}_${quiz.QUIZ_NO}` : null;

        if (!currentQuizKey) return;

        // 퀴즈가 변경되었는지 확인
        const quizChanged = currentQuizKey !== prevQuizKeyRef.current;

        if (quizChanged) {
            prevQuizKeyRef.current = currentQuizKey;
            hasRestoredRef.current = false;

            // 1단계: 무조건 빈 배열로 초기화
            setSelectedChoices([]);
            setShowResult(false);
        }

        // 참여 이력 복원 (퀴즈 변경 시 또는 participation 업데이트 시)
        if (hasParticipated && participation && String(participation.QUIZ_NO) === String(quiz?.QUIZ_NO)) {
            const submittedSql = participation.SBMSN_SQL_CN || participation.USER_QUERY || '';
            const isCorrect = participation.SUCCSS_YN === 'Y';

            setShowResult(true);

            if (submittedSql && submittedSql.trim()) {
                const submittedChoices = submittedSql.split(',').filter(c => c.trim());
                setSelectedChoices(submittedChoices);

                if (!isCorrect) {
                    hasRestoredRef.current = true;
                }
            } else {
                setSelectedChoices([]);
            }
        } else if (!hasParticipated || !participation) {
            // 참여 이력 없음
            if (!quizChanged) {
                setSelectedChoices([]);
                setShowResult(false);
            }
        }
    }, [challengeId, quiz?.QUIZ_NO, hasParticipated, participation]);

    const handleChoiceSelect = (choiceId) => {
        // 제출 가능하고 제출 중이 아닐 때만 선택 가능
        if (canSubmit && !isSubmitting) {
            // 체크박스 토글
            setSelectedChoices(prev => {
                const newSelection = prev.includes(choiceId)
                    ? prev.filter(id => id !== choiceId)
                    : [...prev, choiceId];
                return newSelection;
            });
        }
    };

    // 선택 변경 시 부모에게 알림 (useEffect로 분리)
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedChoices);
        }
    }, [selectedChoices]); // onSelectionChange는 의존성에서 제외 (무한 루프 방지)

    const handleSubmit = () => {
        if (selectedChoices.length === 0) {
            toast.warning(t('challenge:quiz.pickOne'));
            return;
        }

        if (!canSubmit) {
            toast.warning(t('challenge:quiz.tooManyAttempts', { max: maxSubmissions }));
            return;
        }

        // 선택지 배열을 쉼표로 구분된 문자열로 변환하여 USER_QUERY로 전달
        const selectedString = selectedChoices.join(',');
        onSubmit(selectedString);
        setShowResult(true);
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const getChoiceStyle = (choiceId) => {
        const baseStyle = {
            padding: '6px',
            margin: '3px 0',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            cursor: canSubmit ? 'pointer' : 'default',
            transition: 'background 150ms ease, border-color 150ms ease, color 150ms ease',
            background: 'var(--color-bg)'
        };

        const isUserSelected = selectedChoices.includes(choiceId);

        // 제출 불가능한 경우 (정답 또는 제출 횟수 초과)
        if (!canSubmit) {
            if (isUserSelected) {
                return {
                    ...baseStyle,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-quiz-selected-bg)',
                    color: 'var(--color-text)',
                    opacity: 0.7
                };
            } else {
                return {
                    ...baseStyle,
                    opacity: 0.5
                };
            }
        }

        // 제출 가능한 경우
        if (isUserSelected) {
            return {
                ...baseStyle,
                border: '1px solid var(--color-border)',
                background: 'var(--color-quiz-selected-bg)',
                color: 'var(--color-text)'
            };
        }

        return baseStyle;
    };

    if (!quizData) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div>{t('challenge:quiz.loading')}</div>
            </div>
        );
    }

    return (
        <div style={{ padding: '8px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* 퀴즈 헤더 정보 */}
            <div style={{ marginBottom: '6px', padding: '6px 10px', background: 'var(--color-bg-secondary)', borderRadius: '4px', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '13px', fontWeight: '600' }}>{t('challenge:quiz.header', { no: quiz.QUIZ_NO })}</h3>
                        <AttachmentViewer
                            attachmentList={quiz.ATCH_FILE_LST_CN}
                            challengeId={challengeId}
                            quizNo={quiz.QUIZ_NO}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {/* 평가 기준 — 정답 판정·제출 횟수·제한 시간(2026-10-05) */}
                        <QueryRubricButton kind="002" />
                        {/* 잔여 제출 횟수 표시 */}
                        {!isCorrect && (
                            <div style={{
                                padding: '2px 8px',
                                background: remainingSubmissions > 0 ? 'var(--color-accent)' : 'var(--color-text-muted)',
                                color: 'var(--color-bg)',
                                borderRadius: '12px',
                                fontWeight: 'bold',
                                fontSize: '11px'
                            }}>
                                {t('challenge:quiz.attempts', { left: remainingSubmissions, max: maxSubmissions })}
                            </div>
                        )}
                        {/* 타이머 표시 */}
                        {!hasParticipated && timeLeft !== null && (
                            <div style={{
                                padding: '2px 8px',
                                background: timeLeft < 60 ? 'var(--color-quiz-fail-text)' : 'var(--color-accent)',
                                color: 'var(--color-bg)',
                                borderRadius: '12px',
                                fontWeight: 'bold',
                                fontSize: '11px'
                            }}>
                                {formatTime(timeLeft)}
                            </div>
                        )}
                        {/* 결과 표시 */}
                        {hasParticipated && (
                            <div style={{
                                padding: '2px 8px',
                                background: isCorrect ? 'var(--color-quiz-success-bg)' : 'var(--color-quiz-fail-bg)',
                                border: `1px solid ${isCorrect ? 'var(--color-quiz-success-border)' : 'var(--color-quiz-fail-border)'}`,
                                color: isCorrect ? 'var(--color-quiz-success-text)' : 'var(--color-quiz-fail-text)',
                                borderRadius: '12px',
                                fontWeight: 'bold',
                                fontSize: '11px'
                            }}>
                                {isCorrect ? t('challenge:quiz.correct') : t('challenge:quiz.wrong')}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 문제 내용과 선택지 - 가로 배치 (2/3 : 1/3) */}
            <div style={{ flex: 1, display: 'flex', gap: '8px', minHeight: 0, maxHeight: 'calc(100% - 50px)' }}>
                {/* 왼쪽: 문제 영역 (2/3) - Markdown 렌더링 */}
                <div style={{
                    flex: '0 0 66.66%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: '100%'
                }}>
                    <div style={{
                        flex: 1,
                        padding: '8px',
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '100%',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none'
                    }}>
                        <div style={{
                            lineHeight: '1.5',
                            fontSize: '12px',
                            color: 'var(--color-text)'
                        }} className="markdown-content">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    // 코드 블록 스타일링
                                    code({node, inline, className, children, ...props}) {
                                        // react-markdown 새 판은 `inline` 을 넘기지 않는다 — 늘 블록으로 그려 문장 속 `코드` 가
                                        // 줄을 끊고 상자로 떠 있었다(2026-10-05). 언어 표시도 줄바꿈도 없으면 문장 속 코드다
                                        const isInline = inline ?? (!/language-/.test(className || '') && !String(children).includes('\n'));
                                        return isInline ? (
                                            <code style={{
                                                background: 'var(--color-bg-secondary)',
                                                padding: '1px 4px',
                                                borderRadius: '2px',
                                                fontSize: '11px',
                                                fontFamily: 'monospace',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }} {...props}>
                                                {children}
                                            </code>
                                        ) : (
                                            <pre style={{
                                                background: 'var(--color-bg-secondary)',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                overflow: 'auto',
                                                fontSize: '11px',
                                                border: '1px solid var(--color-border)',
                                                marginTop: '6px',
                                                marginBottom: '6px',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}>
                                                <code style={{ fontFamily: 'monospace' }} {...props}>
                                                    {children}
                                                </code>
                                            </pre>
                                        );
                                    },
                                    // 헤딩 스타일링
                                    h1: ({node, ...props}) => <h1 style={{ fontSize: '15px', marginTop: '10px', marginBottom: '8px', fontWeight: 'bold' }} {...props} />,
                                    h2: ({node, ...props}) => <h2 style={{ fontSize: '14px', marginTop: '8px', marginBottom: '6px', fontWeight: 'bold' }} {...props} />,
                                    h3: ({node, ...props}) => <h3 style={{ fontSize: '13px', marginTop: '6px', marginBottom: '4px', fontWeight: 'bold' }} {...props} />,
                                    // 리스트 스타일링
                                    ul: ({node, ...props}) => <ul style={{ marginLeft: '16px', marginTop: '4px', marginBottom: '4px', paddingLeft: '0' }} {...props} />,
                                    ol: ({node, ...props}) => <ol style={{ marginLeft: '16px', marginTop: '4px', marginBottom: '4px', paddingLeft: '0' }} {...props} />,
                                    li: ({node, ...props}) => <li style={{ marginBottom: '4px', lineHeight: '1.5' }} {...props} />,
                                    // 단락 스타일링
                                    p: ({node, ...props}) => <p style={{ marginTop: '4px', marginBottom: '4px', lineHeight: '1.5' }} {...props} />,
                                    // 테이블 스타일링
                                    table: ({node, ...props}) => (
                                        <table style={{
                                            borderCollapse: 'collapse',
                                            width: '100%',
                                            marginTop: '6px',
                                            marginBottom: '6px',
                                            border: '1px solid var(--color-border)'
                                        }} {...props} />
                                    ),
                                    th: ({node, ...props}) => (
                                        <th style={{
                                            border: '1px solid var(--color-border)',
                                            padding: '4px 6px',
                                            background: 'var(--color-bg-tertiary)',
                                            fontSize: '11px'
                                        }} {...props} />
                                    ),
                                    td: ({node, ...props}) => (
                                        <td style={{
                                            border: '1px solid var(--color-border)',
                                            padding: '4px 6px',
                                            fontSize: '11px'
                                        }} {...props} />
                                    ),
                                    // 강조 스타일링
                                    strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold', color: 'var(--color-text)' }} {...props} />,
                                    em: ({node, ...props}) => <em style={{ fontStyle: 'italic' }} {...props} />
                                }}
                            >
                                {quizData.question}
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>

                {/* 오른쪽: 선택지 + 제출 버튼 영역 (1/3) */}
                <div style={{
                    flex: '0 0 33.33%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: '100%',
                    gap: '6px'
                }}>
                    {/* 선택지 */}
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        padding: '8px',
                        overflow: 'auto',
                        minHeight: 0,
                        maxHeight: '100%'
                    }}>
                        <div style={{ overflow: 'auto' }}>
                            {quizData.choices.map((choice) => (
                                <div
                                    key={choice.id}
                                    style={getChoiceStyle(choice.id)}
                                    onClick={() => handleChoiceSelect(choice.id)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                        {/* 체크박스 */}
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid var(--color-border)',
                                            borderRadius: '3px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: selectedChoices.includes(choice.id) ? 'var(--color-accent)' : 'var(--color-bg)',
                                            flexShrink: 0,
                                            fontSize: '10px'
                                        }}>
                                            {selectedChoices.includes(choice.id) && (
                                                <FiCheck size={12} style={{ color: 'var(--color-bg)', strokeWidth: 3 }} />
                                            )}
                                        </div>
                                        <div style={{ flex: 1, lineHeight: '1.4', fontSize: '11px', color: 'var(--color-text)', fontWeight: '500' }}>
                                            {choice.text}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 제출 상태 정보 영역 */}
                    {(submitMessage || (hasParticipated && timeLeft === 0)) && (
                        <div style={{
                            textAlign: 'center',
                            padding: '8px',
                            marginBottom: '8px',
                            background: submitMessage
                                ? (submitMessage.includes(t('challenge:quiz.answer')) ? 'var(--color-quiz-success-bg)' : 'var(--color-quiz-fail-bg)')
                                : 'var(--color-quiz-warn-bg)',
                            border: submitMessage
                                ? (submitMessage.includes(t('challenge:quiz.answer')) ? '1px solid var(--color-quiz-success-border)' : '1px solid var(--color-quiz-fail-border)')
                                : '1px solid var(--color-quiz-warn-border)',
                            borderRadius: '4px',
                            color: submitMessage
                                ? (submitMessage.includes(t('challenge:quiz.answer')) ? 'var(--color-quiz-success-text)' : 'var(--color-quiz-fail-text)')
                                : 'var(--color-quiz-warn-text)',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            flexShrink: 0
                        }}>
                            {submitMessage || (timeLeft === 0 && hasParticipated ? t('challenge:quiz.autoSubmitted') : '')}
                        </div>
                    )}

                    {/* 제출 버튼 - 시간 초과와 관계없이 항상 활성 */}
                    {canSubmit && (
                        <div style={{ textAlign: 'center', flexShrink: 0 }}>
                            <button
                                onClick={handleSubmit}
                                disabled={selectedChoices.length === 0 || isSubmitting}
                                style={{
                                    width: '100%',
                                    padding: '8px 16px',
                                    background: selectedChoices.length > 0 && !isSubmitting ? 'var(--color-accent)' : 'var(--color-text-muted)',
                                    color: 'var(--color-bg)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: selectedChoices.length > 0 && !isSubmitting ? 'pointer' : 'not-allowed',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {isSubmitting ? t('challenge:quiz.submitting') : t('challenge:quiz.submit')}
                            </button>
                        </div>
                    )}
                    {/* 제출 횟수 초과 메시지 */}
                    {!canSubmit && !isCorrect && (
                        <div style={{
                            textAlign: 'center',
                            padding: '8px',
                            background: 'var(--color-quiz-fail-bg)',
                            border: '1px solid var(--color-quiz-fail-border)',
                            borderRadius: '4px',
                            color: 'var(--color-quiz-fail-text)',
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }}>
                            <FiAlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('challenge:quiz.noAttemptsLeft')}
                        </div>
                    )}
                </div>
            </div>

            {/* 해설 표시 (참여 후) */}
            {hasParticipated && quiz.QUIZ_SQL_CONTENT && (
                <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px'
                }}>
                    <h5 style={{ margin: '0 0 4px 0', color: 'var(--color-text)', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FiFileText size={12} />{t('challenge:quiz.explanation')}
                    </h5>
                    <div style={{ fontSize: '11px', lineHeight: '1.5', color: 'var(--color-text)' }}>
                        {quiz.QUIZ_SQL_CONTENT}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SingleChoiceQuiz;